/**
 * Fotos schon auf dem Handy verkleinern.
 *
 * Der Server verkleinert ebenfalls, aber das reicht nicht: ohne Verbindung
 * landet das Bild zuerst in IndexedDB. Ein Rundgang mit 30 Aufnahmen à 8 MB
 * wären 240 MB im Browserspeicher – das überschreitet auf iOS das Kontingent
 * und die Aufnahmen wären verloren.
 *
 * Nebeneffekt: Zeichnen auf ein Canvas überträgt keine EXIF-Daten. Die
 * Koordinaten des Spielplatzes verlassen das Handy also gar nicht erst. Der
 * Server entfernt EXIF trotzdem noch einmal – ein Client ist keine
 * Sicherheitsgrenze.
 */

const MAX_KANTE = 1600
const QUALITAET = 0.82

export type VerkleinertesBild = {
  blob: Blob
  breite: number
  hoehe: number
}

/**
 * Liest die Datei, richtet sie nach den EXIF-Angaben auf und verkleinert sie.
 *
 * `createImageBitmap` mit `imageOrientation: 'from-image'` erledigt die
 * Drehung; ohne das liegen Hochkantaufnahmen im Bericht auf der Seite.
 */
export async function verkleinern(datei: File | Blob): Promise<VerkleinertesBild> {
  const bitmap = await createImageBitmap(datei, { imageOrientation: 'from-image' })

  try {
    const faktor = Math.min(1, MAX_KANTE / Math.max(bitmap.width, bitmap.height))
    const breite = Math.round(bitmap.width * faktor)
    const hoehe = Math.round(bitmap.height * faktor)

    const flaeche = document.createElement('canvas')
    flaeche.width = breite
    flaeche.height = hoehe

    const pinsel = flaeche.getContext('2d')
    if (!pinsel) throw new Error('Das Bild konnte nicht verarbeitet werden')

    pinsel.imageSmoothingQuality = 'high'
    pinsel.drawImage(bitmap, 0, 0, breite, hoehe)

    const blob = await new Promise<Blob | null>((fertig) =>
      flaeche.toBlob(fertig, 'image/jpeg', QUALITAET),
    )
    if (!blob) throw new Error('Das Bild konnte nicht gespeichert werden')

    return { blob, breite, hoehe }
  } finally {
    bitmap.close()
  }
}

/** Menschenlesbare Größe für die Anzeige in der Warteschlange. */
export function groesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
